import { Component, DestroyRef, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AlertModule } from 'ngx-bootstrap/alert';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Topic, TopicService } from '@frontend/common';
import { IconPillComponent } from '@frontend/shared';
import { ProfessionalSpecialismsService } from './professional-specialisms.service';
import { SPECIALISMS_MAX_SELECTED } from './specialisms-limits.type';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  selector: 'multisite-professional-specialisms',
  templateUrl: './professional-specialisms.component.html',
  styleUrls: ['./professional-specialisms.component.scss'],
  standalone: true,
  imports: [
    TranslocoModule,
    AlertModule,
    IconPillComponent,
    // LoaderGlobeAnimationDetailComponent,
    TooltipModule
  ],
  providers: [provideTranslocoScope('profile')],
})
export class ProfessionalSpecialismsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly topicService = inject(TopicService);
  readonly specialismsService = inject(ProfessionalSpecialismsService);

  readonly SPECIALISMS_MAX_SELECTED = SPECIALISMS_MAX_SELECTED;

  readonly specialismTopics = toSignal(
    this.topicService.getTopicsObservable('interculturalist', 'specialism'),
    { initialValue: [] as Topic[] },
  );
  readonly selectedTopicIds = this.specialismsService.selectedTopicIds;
  readonly pendingTopicIds = this.specialismsService.pendingTopicIds;
  readonly topicLoading = computed(() => {
    const loadingObject = this.topicService.loadingObjectSignal();
    return loadingObject['category:interculturalist'] === true;
  });
  readonly isAnythingLoading = computed<boolean>(() => this.specialismsService.loadingObjectSignal()[
    'userSelectedSpecialisms'] ||
    this.specialismsService.loadingObjectSignal()['userSpecialisms'] ||
    this.topicLoading());
  readonly error = this.specialismsService.error;

  readonly selectedTopics = computed<Topic[]>(() => {
    const topics = this.specialismTopics();
    const selectedTopicIds = new Set(this.selectedTopicIds());
    return this.sortBySelectionOrder(topics.filter((topic) => selectedTopicIds.has(topic.id)));
  });

  readonly availableTopics = computed<Topic[]>(() => {
    const topics = this.specialismTopics();
    const selectedTopicIds = new Set(this.selectedTopicIds());
    return topics.filter((topic) => !selectedTopicIds.has(topic.id));
  });

  readonly selectedCount = computed(() => this.selectedTopicIds().length);
  readonly remainingSlots = computed(() => SPECIALISMS_MAX_SELECTED - this.selectedCount());

  ngOnInit(): void {
    this.requestSpecialismTopicsIfNeeded();
    this.specialismsService
      .loadSpecialisms(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  isPending(topicId: number): boolean {
    return this.pendingTopicIds().includes(topicId);
  }

  toggleTopic(topic: Topic): void {
    this.specialismsService
      .toggleTopic(topic.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  addTopic(topic: Topic): void {
    if (this.isPending(topic.id) || this.selectedCount() >= SPECIALISMS_MAX_SELECTED) {
      return;
    }

    this.toggleTopic(topic);
  }

  removeTopic(topic: Topic): void {
    if (this.isPending(topic.id)) {
      return;
    }

    this.toggleTopic(topic);
  }

  private requestSpecialismTopicsIfNeeded(): void {
    if (this.specialismTopics().length > 0 || this.topicLoading()) {
      return;
    }

    this.topicService
      // .requestTopics('interculturalist', 'specialism')
      .requestTopics('interculturalist', null) // type is not 'specialism' here because the application should be loading all topics for the interculturalist category, not just the specialism type. This is because the application should be able to display all topics in the specialisms section, not just those that are of the specialism type. We'll filter the topics by type elsewhere for this component.
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  trackById(_: number, topic: Topic): number {
    return topic.id;
  }

  private sortBySelectionOrder(topics: Topic[]): Topic[] {
    const selectedTopicIds = this.selectedTopicIds();
    return [...topics].sort((left, right) => selectedTopicIds.indexOf(left.id) - selectedTopicIds.indexOf(right.id));
  }
}
